import { useContext, useState, useMemo } from 'react';

import './styles.scss';
import PricingGroup from '../../../../components/pricing-group';
import { PricingContext } from '../../../../providers/pricing-provider';
import Select from '../../../../components/select/select';

const RISK_METRIC_OPTIONS = [
  { value: 'DELTA', name: 'Delta' },
  { value: 'GAMMA', name: 'Gamma' },
  { value: 'PV', name: 'PV' },
  { value: 'THETA', name: 'Theta' },
  { value: 'VEGA', name: 'Vega' },
];

const RiskLadder = () => {
  const { trades, getTradeResult, tradeFormData } = useContext(PricingContext);
  const [selectedMetric, setSelectedMetric] = useState<Record<string, string>>({});

  const getMetric = (tradeId: string) => selectedMetric[tradeId] || 'PV';

  const setMetric = (tradeId: string, value: string) => {
    setSelectedMetric((prev) => ({ ...prev, [tradeId]: value }));
  };

  const getTokenName = (tradeId: string): string => {
    const formData = tradeFormData[tradeId];
    if (!formData) return '';
    const underlying = formData.underlying || '';
    const accounting = formData.accounting || '';
    if (!underlying || !accounting) return '';
    return `${underlying}-${accounting}`;
  };

  const shockItems = useMemo(() => {
    for (const trade of trades) {
      const result = getTradeResult(trade.id);
      const shock = result?.risk?.display?.ladder?.SHOCK;
      if (shock?.length) {
        return shock;
      }
    }
    return [];
  }, [trades, getTradeResult]);

  const formatValue = (value: any): string => {
    if (value === undefined || value === null) return '-';
    if (typeof value === 'number') {
      return value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
    return String(value);
  };

  const getSpotValue = (tradeId: string, index: number): string => {
    const result = getTradeResult(tradeId);
    const spotValues = result?.risk?.display?.ladder?.SPOT;
    return formatValue(spotValues?.[index]);
  };

  const getValue = (tradeId: string, index: number): string => {
    const result = getTradeResult(tradeId);
    const metric = getMetric(tradeId);
    const values = result?.risk?.display?.ladder?.[metric];
    return formatValue(values?.[index]);
  };

  return (
    <div className="pricing-data">
      <PricingGroup label={'Risk ladder'} isClosable defaultOpen>
        <div className="content-header">
          <div className="content-header-items">
            <div className="content-header-items-fixed">
              <span>% Change</span>
            </div>
            {trades.map((trade) => {
              const metric = getMetric(trade.id);
              const selectedOption =
                RISK_METRIC_OPTIONS.find((opt) => opt.value === metric) || RISK_METRIC_OPTIONS[0];
              const tokenName = getTokenName(trade.id);

              return (
                <div className="content-header-items-trade" key={trade.id}>
                  <div className="trade-header">
                    <div className="token-name">{tokenName}</div>
                    <div className="column-headers">
                      <span className="column-label">Spot (fixed)</span>
                      <Select
                        values={RISK_METRIC_OPTIONS}
                        defaultValue={selectedOption}
                        onChange={(value) => setMetric(trade.id, value)}
                        className="select-model"
                      />
                    </div>
                  </div>
                </div>
              );
            })}
            <div className="content-header-items-trade"></div>
          </div>
        </div>
        <div className="pricing-data-container">
          <div className="fixed-panel">
            {shockItems.map((item: string, index: number) => (
              <span className="item" key={index}>
                {item}
              </span>
            ))}
          </div>
          <div className="content-panel">
            {trades.map((trade) => (
              <div key={trade.id} className="block">
                {shockItems.map((_: string, index: number) => (
                  <div className="item-row" key={index}>
                    <span className="item-content">{getSpotValue(trade.id, index)}</span>
                    <span className="item-content">{getValue(trade.id, index)}</span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </PricingGroup>
    </div>
  );
};

export default RiskLadder;
